import axios from "axios";

/**
 * Resolves the base URL for backend API calls depending on
 * the current runtime environment.
 *
 * WHY THIS EXISTS:
 * The application needs to talk to different backend endpoints
 * depending on whether it runs locally or in a deployed environment.
 *
 * ENVIRONMENT BEHAVIOR:
 * - development → talk to the FarmerApp SPA, that is running locally
 * - alpha / production  →  use the FarmerApp Url for the respective
 *                          environment, supplied in the .env files.
 *
 * ARCHITECTURE NOTE:
 * We intentionally use raw axios here instead of the shared axiosClient.
 *
 * Reasons:
 * - Avoid circular dependencies during early app startup
 * - Avoid enforced authentication for endpoints that must be
 *   reachable before auth state is known (e.g. maintenance checks)
 */
const getBaseUrl = () => {
  return import.meta.env.VITE_ENV === "development"
    ? import.meta.env.VITE_FARMER_API_URL
    : import.meta.env.VITE_CALLBACK_URL;
};

const joinUrl = (baseUrl, path) => {
  const normalizedBase = String(baseUrl ?? "").replace(/\/+$/, "");
  const normalizedPath = String(path ?? "").replace(/^\/+/, "");
  return `${normalizedBase}/${normalizedPath}`;
}

/**
 * Service responsible for fetching global application state
 * from the backend.
 *
 * RESPONSIBILITY:
 * - Load application-wide settings
 * - Used very early during app initialization
 *
 * CONSTRAINTS:
 * - No authentication
 * - No shared axios interceptors
 * - Minimal dependencies
 *
 * This keeps the startup path predictable and robust.
 */
class ApplicationStateApiService {
  /**
   * Fetches application settings from the backend.
   *
   * IMPLEMENTATION DETAILS:
   * - Uses a hard timeout to avoid blocking the UI
   * - Explicit Content-Type for clarity and consistency
   *
   * @returns {Promise<AxiosResponse>}
   */
  getAppSettings = async () => {
    return axios.get(joinUrl(getBaseUrl(), "/api/v1/AppSettings"), {
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-store",
        Pragma: "no-cache",
      },
      timeout: 5000,
    });
  };
}

export default new ApplicationStateApiService();
